type LogLevel = 'debug' | 'info' | 'warn' | 'error';
type LogCategory = 'drag' | 'api' | 'render' | 'upload' | 'general' | 'queue' | 'copy';

interface LogEntry {
  category: LogCategory;
  level: LogLevel;
  message: string;
  data?: unknown;
}

class Logger {
  private enabled: boolean | null = null;
  private categoryColors: Record<LogCategory, string> = {
    drag: '#3b82f6',     // blue
    api: '#10b981',      // green
    render: '#8b5cf6',   // purple
    upload: '#f59e0b',   // amber
    general: '#6b7280',  // gray
    queue: '#ec4899',    // pink
    copy: '#06b6d4',     // cyan
  };

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (typeof window !== 'undefined') {
      localStorage.setItem('debugMode', enabled ? 'true' : 'false');
    }
  }

  isEnabled(): boolean {
    // If enabled state has been explicitly set, use that
    if (this.enabled !== null) {
      return this.enabled;
    }
    // Otherwise check localStorage on first access
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('debugMode') === 'true';
      this.enabled = stored;
      return stored;
    }
    return false;
  }

  private log(entry: LogEntry): void {
    if (!this.isEnabled()) return;

    const color = this.categoryColors[entry.category];
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const prefix = `%c[${timestamp}] [${entry.category.toUpperCase()}]`;
    const style = `color: ${color}; font-weight: bold;`;

    switch (entry.level) {
      case 'debug':
        if (entry.data) {
          console.log(prefix, style, entry.message, entry.data);
        } else {
          console.log(prefix, style, entry.message);
        }
        break;
      case 'info':
        if (entry.data) {
          console.info(prefix, style, entry.message, entry.data);
        } else {
          console.info(prefix, style, entry.message);
        }
        break;
      case 'warn':
        if (entry.data) {
          console.warn(prefix, style, entry.message, entry.data);
        } else {
          console.warn(prefix, style, entry.message);
        }
        break;
      case 'error':
        if (entry.data) {
          console.error(prefix, style, entry.message, entry.data);
        } else {
          console.error(prefix, style, entry.message);
        }
        break;
    }
  }

  debug(category: LogCategory, message: string, data?: unknown): void {
    this.log({ category, level: 'debug', message, data });
  }

  info(category: LogCategory, message: string, data?: unknown): void {
    this.log({ category, level: 'info', message, data });
  }

  warn(category: LogCategory, message: string, data?: unknown): void {
    this.log({ category, level: 'warn', message, data });
  }

  error(category: LogCategory, message: string, data?: unknown): void {
    this.log({ category, level: 'error', message, data });
  }
}

export const logger = new Logger();
